import type { ConceptAssistInput } from '@/api/projectApi'
import type { ConceptDraft, Project } from '@/types/nf'
import type { JobChapterMode } from '@/constants/chapterModes'

export interface EasyChapterValues {
  chapter_number: number
  title?: string
  outline: string
  target_words?: number
  chapter_mode?: JobChapterMode
}

function optionalText(value: string | null | undefined) {
  const trimmed = value?.trim()
  return trimmed || undefined
}

function projectChapterMode(project: Project): JobChapterMode {
  const validModes: JobChapterMode[] = ['regular', 'climax', 'thematic', 'volume_opening', 'volume_finale']
  return validModes.includes(project.outline_mode as JobChapterMode) ? project.outline_mode as JobChapterMode : 'regular'
}

export function createEasyChapterValues(defaultChapterNumber: number, project?: Project | null): EasyChapterValues {
  return {
    chapter_number: defaultChapterNumber,
    title: '',
    outline: '',
    target_words: project?.chapter_word_target ?? 2500,
    chapter_mode: project ? projectChapterMode(project) : 'regular',
  }
}

export function buildChapterDraftAssistInput(project: Project, existingChapterCount: number): ConceptAssistInput {
  return {
    field: 'chapter_draft',
    existing_chapter_count: existingChapterCount,
    genre_key: optionalText(project.genre),
    content_language: optionalText(project.content_language),
    secondary_drivers: project.secondary_drivers ?? undefined,
    logline: optionalText(project.logline),
    selling_point: optionalText(project.description),
    protagonist: {},
    world: {
      time_period: optionalText(project.world_time_period),
      location: optionalText(project.world_location),
      atmosphere: optionalText(project.world_atmosphere),
    },
    use_llm: true,
  }
}

export function applyChapterDraft(values: EasyChapterValues, draft: ConceptDraft): EasyChapterValues {
  const outline = draft.outline?.trim()
  if (!outline) throw new Error('chapter draft outline is empty')
  return {
    ...values,
    title: draft.title?.trim() || '',
    outline,
  }
}
